import { useEffect, useRef } from 'react';
import useEventBus from './useEventBus';

/**
 * Custom hook to track and manage payment information from UDS events.
 * 
 * Listens to:
 * - uds:PaymentCalcResponse:Update - Updates payment calculation data
 * - uds:PaymentObject:Update - Updates downPayment from payment objects
 * 
 * @returns {React.RefObject} - A ref object containing current payment info
 */
const usePaymentsInfo = ({ enabled = true } = {}) => {
	const [eventBus, isReady] = useEventBus();
	const paymentsInfoRef = useRef({});

	useEffect(() => {
		if (enabled && isReady) {
			const handleCalcResponse = (data) => {
				if (data?.creditType) {
					paymentsInfoRef.current = {
						...paymentsInfoRef.current,
						creditType: data.creditType,
						...(data.dealType && { dealType: data.dealType }),
						...(data.loanTerm && { loanTerm: data.loanTerm }),
						...(data.leaseTerm && { leaseTerm: data.leaseTerm }),
						tradeIn: data.tradeIn || {
							value: null,
							amountOwed: null,
							condition: null,
							source: null,
							ico: []
						},
						icoTradeIn: data.icoTradeIn || {},
						penciling: data.penciling || {}
					};
				}
			};

			const handlePaymentObjectUpdate = (paymentObjects) => {
				if (!Array.isArray(paymentObjects)) return;
				const dealType = paymentsInfoRef.current?.dealType || 'finance';
				const match = paymentObjects.find((p) => p.paymentLabel === dealType);
				if (match?.downPayment != null) {
					paymentsInfoRef.current = {
						...paymentsInfoRef.current,
						downPayment: match.downPayment
					};
				}
			};

			const handleCalcPencilingDataUpdate = (paymentObjects) => {
				if (paymentObjects[0] != null && paymentObjects[0].dealType != null) {
					paymentsInfoRef.current = {
						...paymentsInfoRef.current,
						dealType: paymentObjects[0].dealType,
					};
				}
			};

			eventBus.on('uds:PaymentCalcResponse:Update', handleCalcResponse);
			eventBus.on('uds:PaymentObject:Update', handlePaymentObjectUpdate);
			eventBus.on('uds:calcPencilingDataUpdate:updated', handleCalcPencilingDataUpdate);

			return () => {
				eventBus.off('uds:PaymentCalcResponse:Update', handleCalcResponse);
				eventBus.off('uds:PaymentObject:Update', handlePaymentObjectUpdate);
				eventBus.off('uds:calcPencilingDataUpdate:updated', handleCalcPencilingDataUpdate);
			};
		}
		return undefined;
	}, [enabled, eventBus, isReady]);

	return paymentsInfoRef;
};

export default usePaymentsInfo;
